import {useEffect, useRef} from 'react';

export interface StreamBufferProgress {
  nextSeq: number;
  lastProgressAt: number | null;
  gapSince: number | null;
  /** Subscription payloads received, including the initial replay snapshot. */
  payloadCount: number;
  terminal: boolean;
}

const GAP_GRACE_MS = 500;
const SNAPSHOT_ONLY_MS = 5_000;
const MAX_BACKOFF_MS = 30_000;
const CHECK_INTERVAL_MS = 250;

interface StreamRecoveryOptions {
  streamUuid: string | null;
  isStreaming: boolean;
  getProgress: () => StreamBufferProgress;
  restart: () => void;
}

/**
 * Restart only the transport; the same stream's buffer and accumulators survive.
 *
 * Two independent triggers:
 * - A seq gap that outlives the reordering grace period.
 * - Silence on a subscription that has only ever delivered its replay
 *   snapshot. Events emitted between that snapshot and the channel's
 *   registration are lost, and if the stream ended inside that window no
 *   later event will ever expose the loss. The first live payload proves
 *   registration completed, after which silence is trusted indefinitely.
 */
export function useStreamRecovery(options: StreamRecoveryOptions): void {
  const {streamUuid, isStreaming} = options;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!streamUuid || !isStreaming) {
      return;
    }

    const startedAt = Date.now();
    const initial = optionsRef.current.getProgress();
    let nextSeq = initial.nextSeq;
    let payloadsBeforeSubscribe = initial.payloadCount;
    let retryAfter = 0;
    let backoff = SNAPSHOT_ONLY_MS;

    const check = () => {
      const current = optionsRef.current;
      if (current.streamUuid !== streamUuid || !current.isStreaming) {
        return;
      }

      const progress = current.getProgress();
      if (progress.terminal) {
        return;
      }

      if (progress.nextSeq !== nextSeq) {
        nextSeq = progress.nextSeq;
        retryAfter = 0;
        backoff = SNAPSHOT_ONLY_MS;
      }

      const now = Date.now();
      const gapExpired =
        progress.gapSince !== null && now - progress.gapSince >= GAP_GRACE_MS;
      const registered = progress.payloadCount > payloadsBeforeSubscribe + 1;
      const snapshotOnly =
        !registered &&
        now - (progress.lastProgressAt ?? startedAt) >= SNAPSHOT_ONLY_MS;
      if (now < retryAfter || (!gapExpired && !snapshotOnly)) {
        return;
      }

      retryAfter = now + backoff;
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      payloadsBeforeSubscribe = progress.payloadCount;
      current.restart();
    };

    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [streamUuid, isStreaming]);
}
